'use client';

import { useTranslations } from 'next-intl';
import { Badge, Text, Tooltip } from '@terra/ui-design-system';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { Activity01Icon, Globe02Icon, UserIcon } from '@terra/ui-icons';
import { useProjectStore } from '../../store/projectStore';
import { useCredentialsPreview } from '../../hooks/useCredentialsPreview';

export function TrackingStatusIndicator() {
  const t = useTranslations('layout.trackingStatus');
  const tSource = useTranslations('proxy.endpointDetails.sourceValues');
  const {
    isTrackingEnabled,
    trackingCredentialId,
    trackingConfig,
    selectedTarget,
  } = useProjectStore();
  const { credentialsPreview } = useCredentialsPreview({
    applicationId: selectedTarget?.id,
  });
  const activeCredential = trackingCredentialId
    ? credentialsPreview.find((cred) => cred.id === trackingCredentialId)
    : null;

  if (!isTrackingEnabled) {
    return null;
  }

  const isPublicAccess =
    !activeCredential && trackingCredentialId === undefined;
  const tooltipContent = (
    <VStack gap={1} alignItems="flex-start" p={1}>
      <Text fontSize="xs" fontWeight="medium">
        Automatic Tracking Active
      </Text>
      {activeCredential && (
        <Text fontSize="2xs" color="fg.muted">
          Using credential: {activeCredential.username} ({activeCredential.role}
          )
        </Text>
      )}
      {isPublicAccess && (
        <Text fontSize="2xs" color="fg.muted">
          Tracking as unauthenticated user
        </Text>
      )}
      {trackingConfig && (
        <VStack gap={0.5} alignItems="flex-start" mt={1}>
          {trackingConfig.recordNetworkTraffic && (
            <Text fontSize="2xs" color="fg.muted">
              • Network traffic recording
            </Text>
          )}
          {trackingConfig.recordPageScreenshots && (
            <Text fontSize="2xs" color="fg.muted">
              • Page screenshots
            </Text>
          )}
        </VStack>
      )}
    </VStack>
  );

  return (
    <Tooltip
      openDelay={50}
      positioning={{
        placement: 'bottom',
        strategy: 'fixed',
      }}
      customContent={tooltipContent}
    >
      <Badge
        variant="fill"
        size="sm"
        bg={token('colors.bg.severity.low')}
        borderColor={token('colors.border.severity.low')}
      >
        <HStack gap={1} alignItems="center">
          <Activity01Icon size={12} />
          <Box as="span" fontSize="xs">
            {tSource('tracking')}
          </Box>
          {activeCredential ? (
            <>
              <Box as="span" fontSize="xs">
                •
              </Box>
              <UserIcon size={12} />
              <Box as="span" fontSize="xs" maxW="80px" truncate>
                {activeCredential.username}
              </Box>
            </>
          ) : null}
          {isPublicAccess ? (
            <>
              <Box as="span" fontSize="xs">
                •
              </Box>
              <Globe02Icon size={12} />
              <Box as="span" fontSize="xs">
                {t('public')}
              </Box>
            </>
          ) : null}
        </HStack>
      </Badge>
    </Tooltip>
  );
}
